import { useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Card, Typography, Box, Divider, IconButton, Stack, Tooltip } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUserGroup, faPlateUtensils, faClock, faLocationDot, faTruck } from "@fortawesome/pro-light-svg-icons"
import { faEllipsisV, faCalendarCheck, faLock, faMugSaucer } from "@fortawesome/pro-solid-svg-icons"
import { MenuMeeting, ReadonlyOrder } from "../../../weekMenuTypes"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { MeetingProgressBar } from "./MeetingProgressBar"
import { LocalizedEnum, LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { SectionWithIcon } from "../../SectionWithIcon"
import type { MouseEvent } from "react"
import { useAppSelector } from "../../../../../../store/storeHooks"
import { selectEffectiveUserId, selectEffectiveCompanyId } from "../../../../../../context/contextSelectors"
import { MeetingCardMenu } from "./MeetingCardMenu"
import { tailwindPalette } from "../../../../../../theme/tailwindPalette"
import { DeliveryType } from "../../../../../../merchant/merchant/production/meetingService/meetingServiceTypes"
import { SimpleStatusChip } from "../../../../../../merchant/merchant/production/meetingService/components/StatusMenu"
import { OrderChatIcon } from "../../../../../../../shared/components/orderChat/OrderChatIcon"
import { OrderChatDrawer } from "../../../../../../../shared/components/orderChat/OrderChatDrawer"
import { ChatMessageAuthorRole, UserType } from "../../../../../../../shared/components/orderChat/orderChatTypes"
import { UserMeetingsReceipt } from "../../../../MeetingsReceipt/UserMeetingsReceipt"

const useStyles = makeStyles()(() => ({
    card: {
        position: "relative",
        borderRadius: ".5rem",
        backgroundColor: "#fff",
        padding: "1rem",
        boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
        width: "100%",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: ".5rem",
    },
    divider: {
        margin: ".75rem 0",
    },
    iconWrapper: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: ".5rem",
        padding: ".7rem",
        width: "2.5rem",
        height: "2.5rem",
    },
    meetingDetailsIcon: {
        color: tailwindPalette.purple[900],
        backgroundColor: tailwindPalette.purple[200],
    },
    meetingServiceIcon: {
        color: tailwindPalette.teal[900],
        backgroundColor: tailwindPalette.teal[200],
    },
    iconXs: {
        fontSize: "1.125rem",
        color: "#94A3B8",
        verticalAlign: "baseline",
    },
    chipContainer: {
        marginBottom: "1rem",
    },
    chipLabel: {
        fontSize: "0.875rem",
        lineHeight: 1.2,
    },
    moreMenu: {
        position: "absolute",
        top: "1rem",
        right: "1rem",
    },
}))

const mapMeeting = (meeting: ReadonlyOrder): MenuMeeting => {
    const { meetingName, endDateTime, startDateTime } = meeting.meetingDetails

    return {
        id: meeting.id,
        uid: meeting.uid,
        orderSummary: meeting.orderSummary,
        deliveryDetails: {
            time: meeting.deliveryDetails.time || startDateTime, // used in receipt header select
        },
        organizers: meeting.organizers,
        meetingDetails: {
            meetingName,
            startDateTime: meeting.deliveryDetails.time,
            endDateTime,
        },
        messages: meeting.messages,
    }
}

export function MeetingCard({ meeting, meetings }: { meeting: ReadonlyOrder; meetings: ReadonlyOrder[] }) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const userId = useAppSelector(selectEffectiveUserId)
    const companyId = useAppSelector(selectEffectiveCompanyId)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [chatDialogOpen, setChatDialogOpen] = useState(false)
    const [showReceiptModal, setShowReceiptModal] = useState(false)

    const { id, messages, meetingDetails, deliveryDetails, orderSummary } = meeting
    const { meetingName, meetingTimeAsText, location: meetingLocation, startDateTime, endDateTime } = meetingDetails
    const { type: deliveryType, location: deliveryLocation, isLocked, statusDetails, time, sittings } = deliveryDetails
    const { hasBooking, hasCatering, participants } = orderSummary

    const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const menuMeeting = useMemo((): MenuMeeting => mapMeeting(meeting), [meeting])
    const allMeetingsFormatted = useMemo((): MenuMeeting[] => meetings.map(mapMeeting), [meetings])

    return (
        <Card className={classes.card}>
            <MeetingProgressBar start={startDateTime} end={endDateTime} />

            {hasBooking && (
                <Stack direction="row" spacing="1rem" alignItems="start">
                    <Box className={cx(classes.iconWrapper, classes.meetingDetailsIcon)}>
                        <FontAwesomeIcon icon={faCalendarCheck} color="a855f7" />
                    </Box>
                    <Box width="100%">
                        <Box className={classes.header}>
                            <Stack direction="row" alignItems="center" gap="0.5rem">
                                <Typography variant="subtitle1">{meetingName}</Typography>
                                {!hasCatering && (
                                    <OrderChatIcon messages={messages} onClick={() => setChatDialogOpen(true)} />
                                )}
                            </Stack>
                        </Box>

                        <Typography variant="body2" color="textSecondary">
                            {meetingTimeAsText}
                        </Typography>

                        {(meetingLocation?.trim() || !!participants) && <Divider className={classes.divider} />}
                        {meetingLocation?.trim() && (
                            <SectionWithIcon icon={faLocationDot}>
                                <Typography variant="body2" color="textSecondary">
                                    {meetingLocation}
                                </Typography>
                            </SectionWithIcon>
                        )}
                        {!!participants && (
                            <SectionWithIcon icon={faUserGroup}>
                                <LocalizedStrict
                                    id="user-home-upcoming-meeting-service-attendees"
                                    vars={{ attendees: participants }}
                                >
                                    <Typography variant="body2" color="textSecondary">
                                        {participants} participants
                                    </Typography>
                                </LocalizedStrict>
                            </SectionWithIcon>
                        )}
                    </Box>
                </Stack>
            )}

            {hasCatering && (
                <>
                    {hasBooking && <Divider className={classes.divider} />}
                    <Stack direction="row" spacing="1rem" alignItems="start">
                        <Box className={cx(classes.iconWrapper, classes.meetingServiceIcon)}>
                            <FontAwesomeIcon icon={faMugSaucer} />
                        </Box>
                        <Box width="100%">
                            <Box className={classes.header}>
                                <Stack direction="row" alignItems="center" gap="0.5rem">
                                    <Typography variant="subtitle1">{meetingName}</Typography>
                                    <OrderChatIcon messages={messages} onClick={() => setChatDialogOpen(true)} />
                                    {isLocked && (
                                        <LocalizedStrict id="meeting-service-table-lock-column-locked" attrs={{ title: true }}>
                                            <Tooltip title="">
                                                <Box component="span" display="inline-flex">
                                                    <FontAwesomeIcon icon={faLock} className={classes.iconXs} />
                                                </Box>
                                            </Tooltip>
                                        </LocalizedStrict>
                                    )}
                                </Stack>
                            </Box>
                            <SimpleStatusChip
                                className={classes.chipContainer}
                                labelSizeOverride={classes.chipLabel}
                                status={statusDetails.status}
                                tooltipLabel={statusDetails.message}
                                size="small"
                            />
                            <Stack direction="column" spacing=".5rem">
                                <SectionWithIcon icon={faClock}>
                                    <Typography variant="body2" color="textSecondary">
                                        {dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(time))}
                                    </Typography>
                                </SectionWithIcon>
                                {sittings && (
                                    <SectionWithIcon icon={faPlateUtensils}>
                                        <LocalizedStrict
                                            id="user-home-upcoming-meeting-service-sittings"
                                            vars={{ sittings }}
                                        >
                                            <Typography variant="body2" color="textSecondary">
                                                {sittings} seats
                                            </Typography>
                                        </LocalizedStrict>
                                    </SectionWithIcon>
                                )}
                                <SectionWithIcon icon={faTruck}>
                                    <Typography variant="body2" color="textSecondary" lineHeight={1.7}>
                                        {deliveryType === DeliveryType.DELIVERY ? (
                                            <>
                                                <LocalizedEnum base="delivery-type-filter" enumValue={deliveryType}>
                                                    <>{deliveryType}</>
                                                </LocalizedEnum>
                                                : {deliveryLocation.name}
                                            </>
                                        ) : (
                                            <LocalizedEnum base="delivery-type-filter" enumValue={deliveryType}>
                                                <>{deliveryType}</>
                                            </LocalizedEnum>
                                        )}
                                    </Typography>
                                </SectionWithIcon>
                            </Stack>
                        </Box>
                    </Stack>
                </>
            )}

            <Box className={classes.moreMenu}>
                <Box className={classes.header}>
                    <IconButton size="small" onClick={handleMenuOpen}>
                        <FontAwesomeIcon icon={faEllipsisV} />
                    </IconButton>
                </Box>

                {!!anchorEl && (
                    <MeetingCardMenu
                        meeting={menuMeeting}
                        anchorEl={anchorEl}
                        meetings={allMeetingsFormatted}
                        menuOnApi
                        handleMenuClose={handleMenuClose}
                        onChatOpen={() => setChatDialogOpen(true)}
                    />
                )}
            </Box>

            {chatDialogOpen && (
                <OrderChatDrawer
                    open
                    onClose={() => setChatDialogOpen(false)}
                    orderId={id}
                    orderTitle={meetingName}
                    viewerRole={ChatMessageAuthorRole.CUSTOMER}
                    sender={userId && companyId ? { type: UserType.USER, companyId, userId } : undefined}
                    onSubtitleClick={() => setShowReceiptModal(true)}
                />
            )}

            {showReceiptModal && (
                <UserMeetingsReceipt
                    open={showReceiptModal}
                    id={id}
                    meetings={allMeetingsFormatted}
                    onClose={() => setShowReceiptModal(false)}
                />
            )}
        </Card>
    )
}
